import React, { useState } from 'react';
import API_BASE_URL from '../config';
import styles from '../css-isolated/sa_adduser.module.css';
import authFetch from '../utils/authFetch';
import { lettersOnly } from '../utils/inputValidation';

function SaAdduser({ isDarkMode, onSuccess, onError }) {
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    role: '',
    email: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // First name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    // Last name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    // Role validation
    if (!formData.role) {
      newErrors.role = 'User role is required';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    const payload = {
      first_name: formData.firstName,
      middle_name: formData.middleName,
      last_name: formData.lastName,
      user_type: formData.role,
      email: formData.email
    };
    try {
      const response = await authFetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (response.ok && data.success) {
        // Reset form on success
        setFormData({
          firstName: '',
          middleName: '',
          lastName: '',
          role: '',
          email: ''
        });
        
        // Call success callback
        if (onSuccess) {
          onSuccess(`User ${data.data.full_name} has been successfully created!`);
        }
      } else {
        // Handle validation errors
        if (data.errors) {
          const newErrors = {};
          if (data.errors.first_name) newErrors.firstName = data.errors.first_name[0];
          if (data.errors.last_name) newErrors.lastName = data.errors.last_name[0];
          if (data.errors.role) newErrors.role = data.errors.role[0];
          if (data.errors.email) newErrors.email = data.errors.email[0];
          setErrors(newErrors);
        } else {
          if (onError) {
            onError(data.message || 'Failed to create user. Please try again.');
          }
        }
      }
    } catch (error) {
      console.error('Create user error:', error);
      if (onError) {
        onError('Network error. Please check your connection and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      firstName: '',
      middleName: '',
      lastName: '',
      role: '',
      email: ''
    });
    setErrors({});
  };

  return (
    <div className={`${styles.contentWrapper} ${isDarkMode ? styles.darkMode : styles.lightMode}`}>
      {/* Main Form Section */}
      <div className={styles.formSection}>
          
          {/* Section Header with Icon */}
          <div className={styles.sectionHeader}>
            <div className={styles.sectionIcon}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" stroke="currentColor" strokeWidth="2"/>
                <path d="M20.5899 22C20.5899 18.13 16.7399 15 11.9999 15C7.25991 15 3.40991 18.13 3.40991 22" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
            <div>
              <h3 className={styles.sectionTitle}>Personal Information</h3>
              <div className={styles.sectionAccent}></div>
            </div>
          </div>

          {/* Error Alert */}
          {errors.general && (
            <div className={styles.errorAlert}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2"/>
              </svg>
              {errors.general}
            </div>
          )}

          <form onSubmit={handleSubmit} className={styles.formGrid}>
            {/* Personal Information Section */}
            <div className={styles.formRow}>
              <div className={styles.formField}>
                <label htmlFor="firstName" className={styles.formLabel}>
                  First Name <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  onKeyDown={lettersOnly}
                  className={`${styles.formInput} ${errors.firstName ? styles.error : ''}`}
                  placeholder="Enter first name"
                />
                {errors.firstName && (
                  <span className={styles.errorMessage}>{errors.firstName}</span>
                )}
              </div>

              <div className={styles.formField}>
                <label htmlFor="middleName" className={styles.formLabel}>
                  Middle Name <span className={styles.formLabelHint}>(Optional)</span>
                </label>
                <input
                  type="text"
                  id="middleName"
                  name="middleName"
                  value={formData.middleName}
                  onChange={handleInputChange}
                  onKeyDown={lettersOnly}
                  className={styles.formInput}
                  placeholder="Enter middle name"
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formField}>
                <label htmlFor="lastName" className={styles.formLabel}>
                  Last Name <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  onKeyDown={lettersOnly}
                  className={`${styles.formInput} ${errors.lastName ? styles.error : ''}`}
                  placeholder="Enter last name"
                />
                {errors.lastName && (
                  <span className={styles.errorMessage}>{errors.lastName}</span>
                )}
              </div>

              <div className={styles.formField}>
                <label htmlFor="role" className={styles.formLabel}>
                  User Role <span className={styles.required}>*</span>
                </label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className={`${styles.formInput} ${errors.role ? styles.error : ''}`}
                >
                  <option value="">Select user role</option>
                  <option value="unit_nurse">Unit Nurse</option>
                  <option value="chief_nurse">Chief Nurse</option>
                  <option value="warden">Warden</option>
                  <option value="jail_officer">Jail Officer</option>
                  <option value="admin">Admin</option>
                </select>
                {errors.role && (
                  <span className={styles.errorMessage}>{errors.role}</span>
                )}
              </div>
            </div>

            {/* Account Information Section */}
            <div className={styles.sectionDivider}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionIcon}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M22 6L12 13L2 6" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
                <div>
                  <h3 className={styles.sectionTitle}>Account Information</h3>
                  <div className={styles.sectionAccent}></div>
                </div>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formField}>
                <label htmlFor="email" className={styles.formLabel}>
                  Email Address <span className={styles.required}>*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`${styles.formInput} ${errors.email ? styles.error : ''}`}
                  placeholder="Enter email address"
                />
                {errors.email && (
                  <span className={styles.errorMessage}>{errors.email}</span>
                )}
                <span className={styles.helperText}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="currentColor"/>
                  </svg>
                  A randomly generated password will be sent to this email address
                </span>
              </div>

              <div className={styles.formField}>
                {/* Empty field for alignment */}
              </div>
            </div>

            {/* Submit Actions */}
            <div className={styles.submitButtonContainer}>
              <button
                type="button"
                className={styles.resetButton}
                onClick={handleReset}
                disabled={isLoading}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 12C3 12 3 4.5 10.5 4.5C18 4.5 21 12 21 12" stroke="currentColor" strokeWidth="2"/>
                  <path d="M7 8L3 12L7 16" stroke="currentColor" strokeWidth="2"/>
                </svg>
                Reset Form
              </button>
              <button
                type="submit"
                className={`${styles.submitButton} ${isLoading ? styles.loading : ''}`}
                disabled={isLoading}
              >
                <span className={styles.submitButtonText}>
                  {isLoading ? (
                    <>
                      <svg width="16" height="16" className={styles.loadingSpinner} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      Creating User...
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21M12.5 7C12.5 9.48528 10.4853 11.5 8 11.5C5.51472 11.5 3.5 9.48528 3.5 7C3.5 4.51472 5.51472 2.5 8 2.5C10.4853 2.5 12.5 4.51472 12.5 7ZM20 8V14M23 11H17" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      Create User
                    </>
                  )}
                </span>
              </button>
            </div>
          </form>
        </div>
    </div>
  );
}

export default SaAdduser;
